import { type FC, useMemo } from 'react'
import type { ReviewQuestionConfig } from '@customer/gen_proto/api_proto/v1/review_generator_pb'
import { TurnstileWidget } from '@customer/template/ReviewForm/TurnstileWidget'
import type { useReviewForm } from '@customer/template/ReviewForm/useReviewForm'

import {
  AGE_GROUP_OPTIONS,
  buildOptionKey,
  GENDER_OPTIONS,
  QUESTION_SECTIONS,
  type ReviewSectionId,
} from '@modules/review/surveyOptions'
import { Button } from '@modules/shadcn/ui/Button/Button'
import { Card, CardContent } from '@modules/shadcn/ui/Card/Card'
import { Input } from '@modules/shadcn/ui/Input/Input'
import { Label } from '@modules/shadcn/ui/Label/Label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@modules/shadcn/ui/Select/Select'
import { Textarea } from '@modules/shadcn/ui/Textarea/Textarea'
import { PREFECTURE_LABELS } from '@modules/utils/prefectures'

type ReviewFormApi = ReturnType<typeof useReviewForm>

interface ReviewFormProps {
  companyName: string
  config?: ReviewQuestionConfig
  form: ReviewFormApi
  /** Turnstile トークン更新時に親へ通知する（site key 未設定時は呼ばれない） */
  onCaptchaToken: (token: string) => void
  onSubmit: () => void
}

const SECTION_STATE_KEY: Record<ReviewSectionId, 'workTypes' | 'reasons' | 'impressions'> = {
  workType: 'workTypes',
  reason: 'reasons',
  impression: 'impressions',
}

// Radix Select は value="" の SelectItem を許さないため、「選択しない」に戻すための sentinel。
// setField 側で '' に変換して state は空文字（未回答）に保つ。
const NONE = '__none__'

/**
 * config に基づいて表示すべき選択肢を、表示順に整列して返す。
 * - visibility: key が無い = 表示 / false = 非表示
 * - order: config.order[sectionId] の順。未指定や欠落分は元の options 順で後ろに付ける。
 */
const useVisibleOptions = (sectionId: ReviewSectionId, config?: ReviewQuestionConfig) =>
  useMemo(() => {
    const section = QUESTION_SECTIONS.find(s => s.id === sectionId)
    if (!section) return []
    const visible = section.options.filter(option => {
      const key = buildOptionKey(sectionId, option)
      // visibility に明示的に false が入っているものだけ隠す
      return config?.visibility[key] !== false
    })
    const orderItems = config?.order[sectionId]?.items ?? []
    if (orderItems.length === 0) return visible
    const visibleSet = new Set(visible)
    const ordered = orderItems.filter(o => visibleSet.has(o))
    const remaining = visible.filter(o => !orderItems.includes(o))
    return [...ordered, ...remaining]
  }, [sectionId, config])

const SelectionSection: FC<{
  sectionId: ReviewSectionId
  label: string
  max?: number
  options: string[]
  selected: string[]
  onToggle: (option: string) => void
}> = ({ label, max, options, selected, onToggle }) => {
  const atCap = max !== undefined && selected.length >= max
  return (
    <fieldset className="space-y-3">
      <legend className="text-sm font-semibold text-foreground">
        {label}
        {max !== undefined ? (
          <span className="ml-2 text-xs font-normal text-muted-foreground">
            （最大{max}つ／{selected.length}選択中）
          </span>
        ) : (
          <span className="ml-2 text-xs font-normal text-muted-foreground">（複数選択可）</span>
        )}
      </legend>
      <div className="flex flex-wrap gap-2">
        {options.map(option => {
          const isSelected = selected.includes(option)
          const disabled = !isSelected && atCap
          return (
            <Button
              key={option}
              type="button"
              size="sm"
              variant={isSelected ? 'default' : 'outline'}
              disabled={disabled}
              aria-pressed={isSelected}
              onClick={() => onToggle(option)}
            >
              {option}
            </Button>
          )
        })}
      </div>
    </fieldset>
  )
}

/** 施主向けアンケートフォーム（設計 §1.1） */
export const ReviewForm: FC<ReviewFormProps> = ({
  companyName,
  config,
  form,
  onCaptchaToken,
  onSubmit,
}) => {
  const { state, toggleSelection, setField, setPermissionToPost, isValid, isGenerating } = form

  const workTypeOptions = useVisibleOptions('workType', config)
  const reasonOptions = useVisibleOptions('reason', config)
  const impressionOptions = useVisibleOptions('impression', config)

  // permission_to_post_visible は明示的に false のときのみ非表示。config 未取得時は表示。
  const permissionVisible = config?.permissionToPostVisible !== false

  const sectionMeta = useMemo(
    () => ({
      workType: QUESTION_SECTIONS.find(s => s.id === 'workType'),
      reason: QUESTION_SECTIONS.find(s => s.id === 'reason'),
      impression: QUESTION_SECTIONS.find(s => s.id === 'impression'),
    }),
    [],
  )

  return (
    <div className="min-h-screen bg-muted">
      <header className="border-b bg-background px-4 py-4 sm:px-6">
        <div className="mx-auto max-w-2xl">
          <h1 className="text-lg font-semibold text-foreground">口コミ作成</h1>
          <p className="mt-1 text-sm text-muted-foreground">{companyName}</p>
        </div>
      </header>

      <main className="mx-auto max-w-2xl space-y-4 px-4 py-6 sm:px-6">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm leading-relaxed text-foreground">
              この度は{companyName}
              に工事をご依頼いただき、誠にありがとうございます。ぜひアンケートにご回答のうえ、同様のお悩みを抱えてらっしゃる方のご参考となるよう、口コミをお寄せいただけますと幸いです。
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-8 p-6">
            <fieldset className="space-y-3">
              <legend className="text-sm font-semibold text-foreground">
                工事エリア
                <span className="ml-2 text-xs font-normal text-muted-foreground">（任意）</span>
              </legend>
              <div className="flex flex-col gap-3 sm:flex-row">
                <div className="space-y-1 sm:w-1/2">
                  <Label htmlFor="review-prefecture" className="text-xs text-muted-foreground">
                    都道府県
                  </Label>
                  <Select
                    value={state.prefecture}
                    onValueChange={v => setField('prefecture', v === NONE ? '' : v)}
                  >
                    <SelectTrigger id="review-prefecture">
                      <SelectValue placeholder="都道府県を選択" />
                    </SelectTrigger>
                    <SelectContent>
                      {/* 任意項目なので、選択後に未選択へ戻せるよう「選択しない」を先頭に置く */}
                      <SelectItem value={NONE}>選択しない</SelectItem>
                      {PREFECTURE_LABELS.map(pref => (
                        <SelectItem key={pref} value={pref}>
                          {pref}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1 sm:w-1/2">
                  <Label htmlFor="review-city" className="text-xs text-muted-foreground">
                    市区町村
                  </Label>
                  <Input
                    id="review-city"
                    value={state.city}
                    maxLength={form.constants.CITY_MAX}
                    placeholder="例）世田谷区"
                    onChange={e => setField('city', e.target.value)}
                  />
                </div>
              </div>
            </fieldset>

            <SelectionSection
              sectionId="workType"
              label={sectionMeta.workType?.label ?? '工事内容'}
              max={sectionMeta.workType?.max}
              options={workTypeOptions}
              selected={state.workTypes}
              onToggle={option =>
                toggleSelection(SECTION_STATE_KEY.workType, option, sectionMeta.workType?.max)
              }
            />

            <SelectionSection
              sectionId="reason"
              label={sectionMeta.reason?.label ?? '当社を選んだ理由'}
              max={sectionMeta.reason?.max}
              options={reasonOptions}
              selected={state.reasons}
              onToggle={option =>
                toggleSelection(SECTION_STATE_KEY.reason, option, sectionMeta.reason?.max)
              }
            />

            <SelectionSection
              sectionId="impression"
              label={sectionMeta.impression?.label ?? '特に印象に残った点'}
              max={sectionMeta.impression?.max}
              options={impressionOptions}
              selected={state.impressions}
              onToggle={option =>
                toggleSelection(SECTION_STATE_KEY.impression, option, sectionMeta.impression?.max)
              }
            />

            <div className="space-y-2">
              <Label htmlFor="review-motivation" className="text-sm font-semibold">
                工事のきっかけ・困っていたこと（任意）
              </Label>
              <Textarea
                id="review-motivation"
                value={state.motivation}
                maxLength={form.constants.DETAIL_MAX}
                placeholder="例）台風のあと雨漏りが気になって相談しました。"
                onChange={e => setField('motivation', e.target.value)}
              />
              <p className="text-right text-xs text-muted-foreground">
                {state.motivation.length} / {form.constants.DETAIL_MAX}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="review-highlight" className="text-sm font-semibold">
                特に印象に残ったこと（任意）
              </Label>
              <Textarea
                id="review-highlight"
                value={state.highlight}
                maxLength={form.constants.DETAIL_MAX}
                placeholder="例）写真で施工前後を見せてくれて分かりやすかったです。"
                onChange={e => setField('highlight', e.target.value)}
              />
              <p className="text-right text-xs text-muted-foreground">
                {state.highlight.length} / {form.constants.DETAIL_MAX}
              </p>
            </div>

            <fieldset className="space-y-3">
              <legend className="text-sm font-semibold text-foreground">
                あなたについて
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  （任意・文章のトーン調整に使います）
                </span>
              </legend>
              <div className="flex flex-col gap-3 sm:flex-row">
                <div className="space-y-1 sm:w-1/2">
                  <Label htmlFor="review-age-group" className="text-xs text-muted-foreground">
                    年代
                  </Label>
                  <Select
                    value={state.ageGroup || NONE}
                    onValueChange={v => setField('ageGroup', v === NONE ? '' : v)}
                  >
                    <SelectTrigger id="review-age-group">
                      <SelectValue placeholder="選択しない" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>選択しない</SelectItem>
                      {AGE_GROUP_OPTIONS.map(age => (
                        <SelectItem key={age} value={age}>
                          {age}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1 sm:w-1/2">
                  <Label htmlFor="review-gender" className="text-xs text-muted-foreground">
                    性別
                  </Label>
                  <Select
                    value={state.gender || NONE}
                    onValueChange={v => setField('gender', v === NONE ? '' : v)}
                  >
                    <SelectTrigger id="review-gender">
                      <SelectValue placeholder="選択しない" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>選択しない</SelectItem>
                      {GENDER_OPTIONS.map(g => (
                        <SelectItem key={g} value={g}>
                          {g}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </fieldset>

            {permissionVisible && (
              <fieldset className="space-y-3">
                <legend className="text-sm font-semibold text-foreground">
                  ホームページへの施工事例掲載許可
                </legend>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={state.permissionToPost ? 'default' : 'outline'}
                    aria-pressed={state.permissionToPost}
                    onClick={() => setPermissionToPost(true)}
                  >
                    はい
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={!state.permissionToPost ? 'default' : 'outline'}
                    aria-pressed={!state.permissionToPost}
                    onClick={() => setPermissionToPost(false)}
                  >
                    いいえ
                  </Button>
                </div>
              </fieldset>
            )}

            <TurnstileWidget onToken={onCaptchaToken} />

            {form.errorMessage && (
              <p className="text-sm text-destructive" role="alert">
                {form.errorMessage}
              </p>
            )}

            <Button
              type="button"
              className="w-full"
              disabled={!isValid || isGenerating}
              isLoading={isGenerating}
              onClick={onSubmit}
            >
              口コミ文を作成する
            </Button>
            {!isValid && (
              <p className="text-center text-xs text-muted-foreground">
                工事内容・選んだ理由・印象に残った点をそれぞれ1つ以上お選びください。
              </p>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  )
}
